import { motion } from "framer-motion";
import { QrCode, Coins, BarChart3, Gift } from "lucide-react";
import { useTranslation } from "react-i18next";

const HowItWorksSection = () => {
  const { t } = useTranslation();
  
  const steps = [
    {
      icon: QrCode,
      titleKey: "scan",
      color: "indigo",
      bgColor: "bg-indigo-100",
      iconColor: "text-indigo-600",
    },
    {
      icon: Coins,
      titleKey: "earn",
      color: "violet",
      bgColor: "bg-violet-100",
      iconColor: "text-violet-600",
    },
    {
      icon: BarChart3,
      titleKey: "track",
      color: "purple",
      bgColor: "bg-purple-100",
      iconColor: "text-purple-600",
    },
    {
      icon: Gift,
      titleKey: "redeem",
      color: "amber",
      bgColor: "bg-amber-100",
      iconColor: "text-amber-600",
    },
  ];

  return (
    <section 
      id="how-it-works"
      className="section-padding bg-white"
      data-testid="how-it-works-section"
    >
      <div className="container-main">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-2xl mx-auto mb-16"
        >
          <span className="inline-block px-4 py-1.5 rounded-full bg-indigo-50 text-indigo-600 text-sm font-medium mb-4">
            {t('howItWorks.badge')}
          </span>
          <h2 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="how-it-works-headline"
          >
            {t('howItWorks.title')}
          </h2>
          <p className="text-lg text-slate-600">
            {t('howItWorks.subtitle')}
          </p>
        </motion.div>

        {/* Steps */}
        <div className="relative">
          {/* Connection line - desktop only */}
          <div className="hidden lg:block absolute top-24 left-[12.5%] right-[12.5%] h-0.5 bg-gradient-to-r from-indigo-200 via-violet-200 via-purple-200 to-amber-200" />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-6">
            {steps.map((step, index) => (
              <motion.div
                key={step.titleKey}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative"
                data-testid={`step-${step.titleKey}`}
              >
                <div className="text-center">
                  {/* Step number */}
                  <div className="relative inline-flex items-center justify-center mb-6">
                    <div className={`w-20 h-20 rounded-2xl ${step.bgColor} flex items-center justify-center relative z-10`}>
                      <step.icon className={`w-9 h-9 ${step.iconColor}`} />
                    </div>
                    <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-white shadow-md flex items-center justify-center z-20">
                      <span className="text-sm font-bold text-slate-700">{index + 1}</span>
                    </div>
                  </div>

                  {/* Content */}
                  <h3 
                    className="text-xl font-bold text-slate-900 mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    {t(`howItWorks.steps.${step.titleKey}.title`)}
                  </h3>
                  <p className="text-slate-600 text-sm leading-relaxed">
                    {t(`howItWorks.steps.${step.titleKey}.description`)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Visual example */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-20"
        >
          <div className="bg-gradient-to-br from-slate-50 to-indigo-50 rounded-3xl p-8 lg:p-12">
            <div className="grid lg:grid-cols-2 gap-8 items-center">
              {/* Example scenario */}
              <div>
                <span className="text-sm font-medium text-indigo-600 mb-2 block">Example Scenario</span>
                <h3 
                  className="text-2xl font-bold text-slate-900 mb-4"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Sarah visits your café
                </h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-indigo-600">1</span>
                    </div>
                    <p className="text-slate-600">
                      Sarah orders a $12 latte and pastry
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-violet-600">2</span>
                    </div>
                    <p className="text-slate-600">
                      She scans your QR code and earns <strong>120 points</strong>
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-purple-600">3</span>
                    </div>
                    <p className="text-slate-600">
                      She sees she's now <strong>80% toward</strong> a free drink
                    </p>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-bold text-amber-600">4</span>
                    </div>
                    <p className="text-slate-600">
                      Sarah comes back tomorrow to hit her goal 🎉
                    </p>
                  </div>
                </div>
              </div>

              {/* Visual card */}
              <div className="relative">
                <div className="bg-white rounded-2xl shadow-xl shadow-slate-200/50 p-6 max-w-sm mx-auto">
                  <div className="flex items-center justify-between mb-4">
                    <span className="text-sm text-slate-500">Sarah's Progress</span>
                    <span className="badge-gold">⭐ Gold Member</span>
                  </div>
                  <div className="text-center py-4">
                    <p className="text-5xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>
                      2,420
                    </p>
                    <p className="text-sm text-slate-500">Total Points</p>
                  </div>
                  <div className="bg-amber-50 rounded-xl p-4">
                    <div className="flex items-center justify-between text-sm mb-2">
                      <span className="text-slate-600">Next: Free Drink</span>
                      <span className="font-semibold text-amber-600">80 pts to go!</span>
                    </div>
                    <div className="w-full h-3 bg-amber-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        whileInView={{ width: "92%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 1, delay: 0.5 }}
                        className="h-full bg-gradient-to-r from-amber-400 to-amber-500 rounded-full"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default HowItWorksSection;
